<template>
  <b-modal
    :id="modalId"
    no-close-on-backdrop
    size="xl"
    @shown="init"
    @hidden="hideModal"
  >
    <template v-slot:modal-header>
      <div>
        <h5 class="modal-title">{{ modalTitle }}</h5>
        <div>{{ modalSubtitle }}</div>
      </div>
    </template>
    <name-input
      v-if="hasData"
      :all-des="allDes"
      :is-ready="true"
      :should-focus="!deName"
      :name="de.name"
      :de-id="de.id"
      @on-name-input-changed="onDeNameChanged"
    ></name-input>
    <element-description
      :elem-description="de.description"
      @on-element-description-changed="onDeDescriptionChanged"
    ></element-description>
    <div class="summarize-fields-container">
      <label class="query-label">
        {{ fieldsText }}
      </label>
      <div class="fields-list">
        <div
          class="field-select"
          v-for="(field, index) in dataExpFields"
          :key="field.id"
        >
          <text-input
            :id="'s-field-input-' + field.id"
            :focus="index > 0 && field.isEmpty"
            :value="field.userLabel"
            type="text"
            @on-text-input-change="onFieldInputChanged"
          />
          <expressions
            class="f-name"
            :id="'ds-field-' + field.id"
            :data-elements="desClone"
            :selected-source="selectedSource"
            :initial-expression="field.value"
            :is-global="isGlobal"
            :content-type="$attrs.appType"
            @on-expression-field-valid="onConfigExpressionValid"
          />

          <div class="delete-wrapper">
            <svg
              class="delete-row-icon"
              tabindex="0"
              @keydown.enter.stop="deleteField(field.id)"
              @click="deleteField(field.id)"
            >
              <use
                xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#delete"
              ></use>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="add-btn-container">
      <label class="query-label ph-label">
        {{ fieldsText }}
      </label>

      <button
        ref="addFieldBtn"
        class="SFDC_light_blue_text no-user-select add-btn"
        @click="addField"
      >
        {{ addItemText }}
      </button>
    </div>

    <template v-slot:modal-footer>
      <button
        class="modal-btn no-user-select modal-cancel-btn"
        @click="hideModal()"
      >
        {{ cancelText }}
      </button>
      <button
        class="modal-btn no-user-select modal-save-btn"
        :disabled="isDisabled"
        @click="finishQuery()"
      >
        {{ okText }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import TextInput from "@/components/TextInput";
  import { v4 as uuidv4 } from "uuid";
  import { MUTATION_NAME } from "@/static/global-variables";
  import store from "@/store";
  import utils from "@/utils";
  import Expressions from "@/components/Expressions";
  import NameInput from "@/components/NameInput";
  import ElementDescription from "@/components/ElementDescription";

  export default {
    name: "SummarizeEdit",
    components: {
      TextInput,
      Expressions,
      NameInput,
      ElementDescription
    },
    props: {
      appId: String,
      de: Object,
      id: String,
      name: String,
      allDes: Object
    },
    data() {
      return {
        expresionFields: [],
        hasDuplicateName: false,
        hasInvalidExpEntry: false,
        allLocalExp: [],
        desClone: null,
        isGlobal: true,
        level: 1,
        deName: null,
        deDescription: null,
        isNameValid: false,
        elementClone: null
      };
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      dataElement() {
        return this.de;
      },
      dataExpFields() {
        return this.expresionFields;
      },
      isDisabled() {
        return (
          this.hasDuplicateName || this.hasInvalidExpEntry || !this.isNameValid
        );
      },
      hasData() {
        return Boolean(this.elementClone && this.desClone);
      },
      selectedSource() {
        return { fields: {} };
      },
      fieldsText() {
        return this.$t("fields");
      },
      addItemText() {
        return this.$t("add");
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      okText() {
        return this.getMessageText(this.messages.OK);
      },
      modalId() {
        return `summarize-edit-modal_${this.de.id}`;
      },
      modalTitle() {
        return `${this.getMessageText(this.messages.Edit)} ${this.$t(
          utils.getDataElementLabel(this.de.type)
        )}`;
      },
      modalSubtitle() {
        return utils.getModalSubtitle(this.de.type);
      }
    },
    methods: {
      hideModal() {
        if (this.de.notReady) {
          const params = {
            appId: this.de.appId,
            deId: this.de.id
          };
          store.commit(MUTATION_NAME.DELETE_DATA_ELEMENT, params);
        }
        this.$bvModal.hide(this.modalId);
      },
      finishQuery() {
        const formattedExpFields = utils.formatExpressionFields(
          this.expresionFields
        );
        const deInfo = {
          appId: this.appId,
          de: {
            id: this.dataElement.id,
            description: this.deDescription,
            name: this.deName,
            type: this.dataElement.type,
            fields: formattedExpFields
          }
        };

        store.commit(MUTATION_NAME.UPDATE_DE_QUERY, deInfo);
        this.$bvModal.hide(this.modalId);
      },
      init() {
        this.expresionFields = [];
        let component = this;
        this.elementClone = JSON.parse(JSON.stringify(this.de));
        this.desClone = JSON.parse(JSON.stringify(this.allDes));
        this.deName = this.de.name;
        this.deDescription = this.de.description;
        delete this.desClone[component.id];

        for (const [key, value] of Object.entries(this.de.fields)) {
          value.label = key || value.label;
          this.addNewExpression(value);
        }

        if (this.dataExpFields.length === 0) {
          this.addField();
        }
      },
      onFieldInputChanged(val, inputId) {
        const fieldId = inputId.replace("s-field-input-", "");
        this.expresionFields.forEach(field => {
          if (field.id === fieldId) {
            field.userLabel = val;
          }
        });
        this.expresionFields = this.expresionFields.concat([]);
        this.setHasDuplicateName();
      },
      setHasDuplicateName() {
        const labels = this.expresionFields.map(v =>
          v.userLabel ? v.userLabel.toLowerCase() : ""
        );
        if (labels.includes("") || !this.expresionFields.length) {
          this.hasDuplicateName = true; // just set this to true to disable submit
        } else {
          const unique = [...new Set(labels)];
          this.hasDuplicateName = labels.length !== unique.length;
        }
      },

      addField() {
        this.addNewExpression();
      },

      addNewExpression(expression) {
        const label = expression ? expression.label : "";
        this.expresionFields.push({
          id: uuidv4(),
          name: "",
          label: label,
          userLabel: label,
          dataType: "",
          isEmpty: !expression,
          value: expression
        });
        this.setHasInvalidExpEntry();
        this.setHasDuplicateName();
      },

      deleteField(id) {
        let component = this;

        this.$bvModal
          .msgBoxConfirm(this.getMessageText(this.messages.ARE_YOU_SURE), {
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.okText,
            cancelTitle: this.cancelText,
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          })
          .then(value => {
            if (value) {
              component.expresionFields = component.expresionFields.filter(
                field => {
                  return field.id !== id;
                }
              );
              this.setHasInvalidExpEntry();
              this.setHasDuplicateName();
            }
          })
          .catch(err => {
            console.error(err);
            // An error occurred
          });
      },
      onConfigExpressionValid(ctx) {
        const fieldId = ctx.id.replace("ds-field-", "");
        const field = this.expresionFields.find(e => e.id === fieldId);
        field.isEmpty = !ctx.status;
        delete field.confExpression;
        if (ctx.status) {
          field.confExpression = ctx.confExpression;
          field.userLabel = field.userLabel || field.confExpression.label;
        }
        this.expresionFields = this.expresionFields.concat([]);
        this.setHasInvalidExpEntry();
        if (!this.hasInvalidExpEntry) {
          this.setHasDuplicateName();
        }
      },
      setHasInvalidExpEntry() {
        const falseExpEntries = this.expresionFields.map(e => e.isEmpty);
        this.hasInvalidExpEntry =
          falseExpEntries.includes(true) || !this.expresionFields.length;
      },
      onDeNameChanged(isValidName, nameInput) {
        this.deName = nameInput;
        this.isNameValid = isValidName;
      },
      onDeDescriptionChanged(input) {
        this.deDescription = input;
      }
    }
  };
</script>

<style scoped>
  .summarize-fields-container {
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
    min-width: 45rem;
  }

  .add-btn-container {
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
  }

  .add-btn {
    align-self: flex-start;
  }

  .query-label {
    padding-right: 1rem;
    margin-bottom: 0;
  }

  .ph-label {
    visibility: hidden;
  }

  .fields-list {
    flex: 3;
  }

  .field-select {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .field-select .f-name {
    margin-left: 0.5rem;
  }

  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    text-decoration: none;
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE);
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }

  .delete-wrapper {
    margin: 0.35rem 0 0 0.5rem;
  }

  .delete-row-icon {
    height: 1.25rem;
    width: 1.25rem;
    fill: #c23934;
    cursor: pointer;
  }
</style>
